<template>
	<article class="product-page">
		<product-markup
			:product="product"
			:nrReviews="nrReviews"
			:reviews="reviews"
		/>
		<header class="col-xs-12 product-header">
			<div class="px-5 col-xs-12">
				<h1>{{ product.mainTitle }}</h1>
				<p class="text-secondary">
					{{ product.subTitle }} <span v-if="this.product.ifsId"> / {{ tp('CODE', product.id) }} ({{ this.product.ifsId }})</span> <span v-else> {{ tp('CODE', product.id) }}</span>
				</p>
				<div
					v-if="product.averageRating && nrMeterReviews != 0"
					class="d-flex justify-content-between align-items-center"
				>
					<div class="d-flex mr-5 align-items-center">
						<div
							v-if="displayStars"
							class="mr-3 semibold"
						>
							{{ product.averageRating.rating }}
						</div>
						<div
							v-else
							class="mr-3 semibold"
						>
							{{ product.averageRating.rating }}
						</div>
						<div class="d-flex align-items-center">
							<stars
								v-if="displayStars"
								:stars="rating"
								class="product-display-stars"
							/>
							<flames
								v-else
								:flames="rating"
								align
								class="product-display-flames"
							/>
							<span
								class="ml-3 clickable"
								@click.prevent="scrollTo('#p-reviews')"
							>({{ nrMeterReviews }})</span>
						</div>
					</div>
					<add-to-wishlist-btn :product="product" />
				</div>
				<div
					v-else
					class="d-flex justify-content-end mt-3"
				>
					<add-to-wishlist-btn
						:product="product"
						class="mt-3"
					/>
				</div>
			</div>
			<modal
				rootClass="verticula-root"
				:canRootClose="false"
				v-model="isOpen"
				:containerStyle="iframeStyle">
				<iframe
					ref="phoneSubscription"
					title="iframe for phone subscription"
					width="100%"
					height="100%"
					z-index="10"
					frameborder="0"
				/>
			</modal>
			<div class="col-md-3 col-xs-12 px-0 pull-right">
				<modal
					v-if="product.energyMarking"
					class="pull-right energy-marking"
				>
					<template
						slot="label"
					>
						<energy-rating
							:nofold="true"
							:height="43"
							:rating="product.energyMarking.rating"
							:scale="product.energyMarking.scale"
							flipped
							unit="px"
						/>
						<div
							class="d-block text-secondary"
						>
							{{ tp('DATASHEET') }}
						</div>
					</template>
					<energy-datasheet :marking="product.energyMarking" />
				</modal>
				<div
					v-if="showHeroMarking"
					class="pull-right"
					style="width: 117px;"
				>
					<div
						style="width: 100%;"
						class="mt-2 mx-auto text-center"
					>
						<hero-marker
							:nofold="true"
							flipped
							style="width: 100%;"
						/>
					</div>
				</div>
				<price-bubble
					v-if="mq.current < mq.lg"
					:class="{'energy-marking-above': product.energyMarking}"
					:product="product"
					:excludeVat="user.settings.show_prices_excluding_vat"
					style="position: absolute; right: 15px;"
				/>
			</div>
		</header>
		<div class="product-wrapper">
			<div class="product-top">
				<div class="product-top-row">
					<section
						:style="mq.current >= mq.lg ? 'width:60%;' : ''"
						class="product px-5"
						style="min-height: 300px; position: relative"
					>
						<splash
							v-if="product.splashes"
							:splashes="product.splashes"
						/>
						<price-bubble
							v-if="mq.current >= mq.lg && (! (product.discontinued > 0) || inStock)"
							:product="product"
							:excludeVat="user.settings.show_prices_excluding_vat"
							class="ap-tr"
						/>
						<gallery
							:images="product.images"
							:imageName="product.name"
						/>
					</section>
					<section
						v-if="isTrulyDiscontinued"
						:style="mq.current >= mq.lg ? 'width:40%;' : ''"
						class="px-5 discontinued-info"
					>
						<!-- Produkten har utgått -->
						<div class="alert alert-danger">
							<ximg
								src="/api/dynimg/icon/utgatt_gameover/D50855"
								ariaLabel="discontinued"
								role="img"
							/>
							<div class="info">
								<h2>
									{{ tp('DISCONTINUED') }}
								</h2>
								<p class="small">
									{{ tp('DISCONTINUED_DESCRIPTION') }}
								</p>
							</div>
						</div>
						<div class="mt-4 small">
							Pröva att navigera via kategorimenyn eller
							<router-link :to="{ name: 'search' }">sök efter en produkt</router-link>.
						</div>
						<variant-selector
							v-if="product.variants"
							:product="product"
							class="mt-5"
						/>
					</section>
					<section
						v-else
						:style="mq.current >= mq.lg ? 'width:40%;' : ''"
						class="px-5"
					>
						<div
							id="add-product-to-cart"
							class="text-center"
						>
							<product-price
								v-if="showPrice"
								:product="product"
								center
								large
							/>
							<flash-sale
								v-if="showFlashSale"
								:productPrice="product.price"
								:overBookingProtection="product.price.price === product.regularPrice.price"
							/>
							<div v-if="maxQtyPerCustomer">
								Max {{ maxQtyPerCustomer }} per kund
							</div>
							<div v-if="! isReleased">
								Release: {{ releaseDate }}
								<span v-if="showDaysUntilRelease">
									({{ formattedDaysUntilRelease }})
								</span>
							</div>
							<variant-selector
								v-if="product.variants"
								:product="product"
							/>
							<variant-picker
								v-if="product.variants && product.variants.variantGroups"
								:variantGroups="product.variants.variantGroups"
								:variantData="product.variants.list"
								:currentProductId="product.id"
								:currentProductPrice="product.price"
								:currentProduct="product"
							/>
							<radio-picker
								v-if="product.phoneSubscription"
								v-model="pickSubscription"
								class="mb-5"
								:options="subscriptionOptions"
							/>
							<product-tip-display
								v-if="canBuy"
								:product="product"
								:bundleOffers="bundleOffers"
								@update:selectedBundleProducts="v => $emit('update:selectedBundleProducts', v)"
							/>
							<buy-button
								:product="product"
								:insured="insured"
								buttonSize="large"
								width="stretch"
								:subscription="!! (product.phoneSubscription && pickSubscription)"
								:selectedBundleProducts="selectedBundleProducts"
								@triggerInsurance="triggerInsurance"
								@getOffers="getOffers"
								@addPhoneSubscription="addPhoneSubscription"
							/>
							<insurance-popup
								v-if="showInsurancePopup"
								:productCategories="product.categories"
								:insurance="product.insurance"
								:product="product"
								:icon="insuranceIcon"
								:showAccessoriesPopup="showAccessoriesPopup"
								ref="insurancePopup"
								@triggerAccessoriesPopup="triggerAccessoriesPopup"
							/>
							<product-accessories-popup
								:accessories="offers"
								ref="showAccessoriesPopup"
							/>
							<product-delivery-test
								v-if="abTest.showDeliveryInfo == 0 && product.isShippable"
								:product="product"
								:shippingPriceFrom="tp('SHIPPING_INFO', formatPrice(lowestMethodCost, product.price.currency))"
							/>
							<product-delivery
								v-if="abTest.showDeliveryInfo > 0 && product.isShippable"
								:product="product"
								:shippingPriceFrom="tp('SHIPPING_INFO', formatPrice(lowestMethodCost, product.price.currency))"
							/>
							<div class="mt-4" v-else>
								<p
									v-if="showPrice"
									style="line-height: 1em"
								>
									<modal v-if="product.isShippable">
										<b
											slot="label"
											v-html="tp('SHIPPING_INFO', formatPrice(lowestMethodCost, product.price.currency))"
										/>
										<shipping-price-table
											:product="product"
											:shippingClass="product.shippingClass"
											:packageSizeId="product.packageSizeId"
											:priceList="priceList"
										/>
									</modal>
								</p>
							</div>
						</div>
						<store-pickup-only
							v-if="! product.isShippable"
							:text="tp('STORE_PICKUP_ONLY')"
						/>
						<mail-order-only
							v-if="product.isShippable && ! product.isCollectable"
							:text="tp('MAIL_ORDER_ONLY')"
						/>
						<div
							v-if="hasMaxPurchaseLimit"
							class="max-purchase"
						>
							{{ tp('MAX_PURCHASE_LIMIT') }}
						</div>
						<div
							v-if="hasLongDeliveryTime"
							class="small long-delivery"
						>
							<delivery-notice
								:displayText="tp('LONG_DELIVERY_TIME')"
								:infoText="product.longDeliveryNotice"
							/>
						</div>
						<div
							v-if="requiresPrepayment"
							class="text-center"
							style="font-size: 0.7em; color: #444; font-weight: 300;"
						>
							<span class="glyphicon glyphicon-alert" />
							{{ tp('REQUIRES_PREPAYMENT') }}
						</div>
						<div class="panel panel-thick-gray">
							<div class="panel-heading">
								{{ t('STOCK.STATUS') }}
								<stock-legend />
							</div>
							<div
								:key="storeKey + 'A'"
								class="stock panel-body"
							>
								<stock-row
									v-if="product.stock.download"
									:product="product"
									:stock.sync="product.stock"
									store="download"
									noStar
								/>
								<stock-row
									v-else
									:product="product"
									:name="t('STORE.WEB_STOCK')"
									:stock.sync="product.stock"
									:release="product.release"
									class="web-stock"
									store="web"
									noStar
								/>
							</div>
							<template v-if="! prerender && ! product.stock.download">
								<div class="panel-heading my-store">
									{{ t('STOCK.STORES') }}
								</div>
								<div
									:key="storeKey + 'B'"
									class="stock panel-body"
								>
									<stock-row
										v-for="store in storeSuggestions.favourites"
										:product="product"
										:key="'store' + store"
										:store="store"
										:stock.sync="product.stock"
										:release="product.release"
									/>
									<template v-if="storeSuggestions.favourites.length && storeSuggestions.suggestions.length">
										<tr>
											<th colspan="3">
												<hr class="mt-1 mb-3" >
											</th>
										</tr>
									</template>
									<stock-row
										v-for="store in storeSuggestions.suggestions"
										:key="'store' + store"
										:product="product"
										:store="store"
										:stock.sync="product.stock"
										:release="product.release"
									/>
								</div>
							</template>
							<dropdown v-if="! product.stock.download">
								<div
									slot="label"
									class="btn btn-dropdown btn-product-page ptl-0 pb-0 stretch-x r-0"
									style="font-size: 12.6px;"
								>
									{{ t('STOCK.SHOW_ALL_STORES') }}
								</div>
								<div class="panel panel-thick-gray ap-fill-x stock-dropdown">
									<template v-for="(group, index) in userSettings.storeGroupFormat">
										<div
											:key="'stock-heading-' + index"
											class="panel-heading"
										>
											{{ t(group.name) }}
										</div>
										<div
											:key="'stock-body-' + index"
											class="stock panel-body"
										>
											<template v-for="(store, index) in group.stores">
												<tr
													v-if="isNaN(store)"
													:key="'store-type-' + index"
												>
													<td
														colspan="2"
														style="font-weight: bold"
													>
														{{ store }}
													</td>
												</tr>
												<stock-row
													v-else
													:key="'store-id-' + store"
													:product="product"
													:stock.sync="product.stock"
													:store="store"
													:release="product.release"
												/>
											</template>
										</div>
									</template>
								</div>
							</dropdown>
						</div>
					</section>
				</div>
			</div>
			<div
				v-if="productCampaigns.length"
				class="product-campaigns"
			>
				<hr >
				<div
					v-for="productCampaign in productCampaigns"
					:class="['campaign-link-wrap', productCampaign.isDiscounted ? 'campaign' : 'list']"
					:key="productCampaign.campaignId"
				>
					<span class="icon-wrap">
						<img
							:src="priceIconCampaign"
							class="price-icon"
							alt="price-icon"
						>
					</span>
					<a
						v-if="productCampaign.campaignLinkText"
						:href="'/se/campaign/' + productCampaign.campaignId"
						:title="productCampaign.name"
						class="custom-highlight-link"
					>
						{{ productCampaign.campaignLinkText }}
					</a>
					<span
						v-else
						class="standard-higlight-link"
					>
						<span class="highlight-name">{{ productCampaign.name }}</span>
						<a
							:href="'/se/campaign/' + productCampaign.campaignId"
							:title="productCampaign.name"
							class="highlight-link"
						>
							{{ staticCampaignLinkText(productCampaign) }}
						</a>
					</span>
				</div>
				<hr >
			</div>

			<!-- Tips for discontinued products -->
			<div
				v-if="showDiscontinuedTips"
				class="px-5 mt-5"
			>
				<div v-if="hasDiscontinuedTips">
					<strong>Produkter du kanske är intresserad av istället</strong>
					<product-tip-slider
						:products="lists[0].products"
						:loop="false"
						:showProgressBar="true"
						:showProductBorder="false"
						parentElement="product"
					/>
					<div
						v-if="categorySuggestions.length"
						style="margin: 20px 0 30px"
					>
						<strong>Kategorier du kanske är intresserad av</strong>
						<div>
							<div
								v-for="suggestion in categorySuggestions"
								:key="suggestion.id"
								class="col-md-6 my-3"
							>
								<router-link
									:to="suggestion.router"
									class="info-link r-3"
								>
									<div class="p-4 pull-left mr-5 stretch-y category-highlight-name">
										<ximg
											:src="convertToCdnUrl('/api/dynimg/category/' + suggestion.icon + '/' + suggestionIconColor)"
											:ariaLabel="suggestion.name + ' ikon'"
											role="img"
										/>
									</div>
									<div class="category-highlight-description">
										{{ suggestion.name }}
									</div>
								</router-link>
							</div>
							<div class="clearfix" />
						</div>
					</div>
				</div>
			</div>
			<div class="product-content">
				<div class="product-description">
					<!-- Review highlight -->
					<top-review-large
						v-if="product.reviewHighlight"
						:heading="isReleased ? 'Framröstad topprecension' : 'Framröstad hype'"
						:displayProductName="topReviewDisplayProductName"
						:review="product.reviewHighlight"
						:isHype="! isReleased"
					/>
					<!-- Navigation -->
					<ul
						ref="productNav"
						:style="cursorStyling"
						class="product-nav"
					>
						<li>
							<a
								href="#p-overview"
								@click.prevent="moveTo('#p-overview')"
							>
								{{ tp('OVERVIEW') }}
							</a>
						</li>
						<li>
							<a
								href="#p-reviews"
								@click.prevent="moveTo('#p-reviews')"
							>
								{{ tp('REVIEWS') }}
							</a>
						</li>
						<li>
							<a
								href="#p-questions"
								@click.prevent="moveTo('#p-questions')"
							>
								{{ tp('QUESTIONS') }}
							</a>
						</li>
						<li v-if="hasData">
							<a
								href="#p-specs"
								@click.prevent="moveTo('#p-specs')"
							>
								{{ tp('SPECIFICATIONS') }}
							</a>
						</li>
						<li>
							<a
								href="#p-info"
								@click.prevent="moveTo('#p-info')"
							>
								{{ tp('INFO') }}
							</a>
						</li>
					</ul>
					<!-- Product description -->
					<section class="px-5 col-md-12 col-sm-12 col-xs-12 product-info">
						<read-more anchor="p-overview">
							<h2>{{ tp('OVERVIEW') }}</h2>
							<div class="info-collapse pt-5">
								<!-- Fyndware information -->
								<template v-if="product.isFyndware">
									<fyndware-info :product="product" />
									<template v-if="product.fyndwareOf">
										<div
											v-if="$refs.originalDescription && showOriginalDescriptionPanel"
											class="panel panel-body"
											style="padding: 8px 15px"
										>
											<strong>Originalbeskrivning</strong>
											<p class="mb-0">
												Nedan följer originalproduktens specifikation. Observera
												att kartong, tillbehör och garantitid kan skilja sig
												från denna originaltext.
											</p>
										</div>
										<product-description
											class="original-description"
											ref="originalDescription"
											:html="product.fyndwareOf.description"
											:product="product"
										/>
									</template>
								</template>
								<template v-else>
									<!-- Not released disclaimer -->
									<div
										v-if="! isReleased"
										class="mb-4"
									>
										<strong>{{ tp('NOT_RELEASED_DISCLAIMER') }}</strong>
									</div>
									<!-- Product description -->
									<transition name="fade">
										<product-description
											:html="description"
											:product="product"
										/>
									</transition>
								</template>
								<!-- hygiene articel banner -->
								<template v-if="isHygieneArticle">
									<div class="panel hygiene-article">
										<div class="hygiene-article-icon">
											<ximg
												src="/api/dynimg/icon/hygienartikel/131313"
												ariaLabel="hygienartikel ikon"
												role="img"
											/>
										</div>
										<div>
											<div>
												<strong>{{ tp('HYGIENE_ARTICLE') }}</strong>
											</div>
											<div>
												<span v-html="tp('HYGIENE_ARTICLE.PURCHASE_TERMS')" />
												<router-link
													:to="{
														name: 'info',
														params: {
															id: 8,
															name: 'Kopvillkor',
														},
													}"
												>
													Läs mer</router-link>
											</div>
										</div>
									</div>
								</template>
							</div>
						</read-more>
						<!-- @TODO Translate hype texts. -->
						<!-- Reviews and hypes -->
						<review-section
							id="p-reviews"
							:heading="isReleased ? tp('REVIEWS') : 'Prerelease hype'"
							:nrReviews="nrReviews"
							:product="product"
							:upvotedReview="product.reviewHighlight"
							@loaded="onReviewsLoad"
							@failed="onReviewsLoad"
						/>
						<!-- Questions and Answers -->
						<question-answer-section
							id="p-questions"
							:heading="tp('QUESTIONS_AND_ANSWERS')"
							:nrQuestions="nrQuestions"
							:product="product"
							@loaded="onQuestionsLoad"
							@failed="onQuestionsLoad"
						/>

						<!-- Tech spec -->
						<read-more
							v-if="hasData"
							anchor="p-specs"
							buttonTextMore="Visa fler specifikationer"
							buttonTextLess="Visa färre specifikationer"
						>
							<h2>{{ tp('TECHNICAL_SPECIFICATIONS') }}</h2>
							<table
								class="table table-condensed table-striped tech-specs-table"
								style="font-size: 12px"
							>
								<tbody>
									<template v-for="(group, groupName, index) in product.data">
										<th
											:key="'techspec-' + index"
											class="pt-5"
											colspan="2"
										>
											{{ groupName }}
										</th>
										<tr
											v-for="(data, value, index2) in group"
											:key="'group-' + index + '-data-' + index2"
										>
											<td>{{ data.name }}:</td>
											<td>{{ data.value }}</td>
										</tr>
									</template>
								</tbody>
							</table>
						</read-more>

						<!-- GSPR info -->
						<read-more
							anchor="p-info"
							buttonTextMore="Visa mer info"
							buttonTextLess="Visa mindre info"
						>
							<h2>{{ tp('INFO') }}</h2>
							<!-- Product specs table -->
							<div class="mt-5">
								<b>{{ tp('PRODUCT_SPECIFICATIONS') }}</b>
								<table class="table table-condensed table-striped product-specs">
									<tbody>
										<tr v-if="releaseDate">
											<td>{{ tp('RELEASE_DATE') }}</td>
											<td>
												{{ releaseDate }}
												<span v-if="showDaysUntilRelease">
													({{ formattedDaysUntilRelease }})
												</span>
											</td>
										</tr>
										<tr>
											<td>{{ tp('OUR_PRODUCT_ID') }}</td>
											<td>{{ product.id }}</td>
										</tr>
										<tr v-if="product.partNumbers && product.partNumbers.length">
											<td>{{ tp('MANUFACTURER_PRODUCT_ID') }}</td>
											<td>
												<template v-for="(pn, index) in product.partNumbers">
													{{ pn }}
													<br :key="'part-number-divider-' + index" >
												</template>
											</td>
										</tr>
										<tr v-if="product.manufacturer">
											<td>
												{{ tp('MANUFACTURER') }}
											</td>
											<td>
												<a-link :to="manufacturerRoute">
													{{ product.manufacturer.name }}
												</a-link>
											</td>
										</tr>
										<tr v-for="(info, key) in product.gsprInfo" :key="'gspr-' + key">
											<td>{{ tg(key) }}</td>
											<td>
												<template v-for="line of info.split('\n')">
													{{ line }}
													<br>
												</template>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</read-more>
						<div class="clearfix" />
						<auto-scroller
							v-if="! renderTips"
							:viewportMargin="800"
							:trigger="setRenderTips"
							:btnText="'Visa ' + tp('ACCESSORIES').toLowerCase()"
							class="mb-4"
						/>
						<div id="p-more" />
						<!-- Anchor for the scrolling links at the top -->
						<div
							v-if="! (productLists && ! productLists.length) && renderTips && mq.current < mq.lg"
							class="more-products"
						>
							<!-- @TODO translate -->
							<h2>Fler produkter</h2>
							<section
								v-for="(list, index) in productLists"
								:key="'accessories-' + index"
								class="row"
							>
								<h3 class="ml-5 mt-5 mb-2">{{ list.title }}</h3>
								<product-tip-slider
									:products="list.products"
									:showBuyBtn="false"
									:loop="false"
									:showStock="false"
									:compact="mq.current <= mq.sm"
									parentElement="product"
									showProgressBar
								/>
							</section>
						</div>
					</section>
				</div>
				<product-aside
					v-if="mq.current >= mq.lg"
					:productLists="productLists"
				/>
			</div>
			<div class="clearfix" />
		</div>
	</article>
</template>

<script>
import waja from 'waja';
import Tween from 'tween';
import bus from 'eventbus';
import mq, { isDesktop } from 'mediaQuery';
import PHPDate from 'PHPDate';
import abTest from 'abTest';
import store from 'datastore';
import { t } from 'datastore/i18n';
import { mapState, mapGetters } from 'vuex';
import { prerender, restrictedProducts } from 'staticData';
import businessVariables from 'businessVariables';
import { trackImpression } from 'gtm';
import { inStockAnywhere, formatPrice, daysBetweenDates, getStoreSuggestions, displayStarRating, getProductRating, convertToCdnUrl, getExtraProductData, scrollTo, formatProductPrice, validCampaignLength } from 'utils';

export default {
	store,
	props: {
		product: {
			type: Object,
			required: true,
		},
		scrollPos: {
			type: Number,
			required: true,
		},
		docHeight: {
			type: Number,
			required: true,
			default: 0,
		},
		selectedBundleProducts: {
			type: Array,
			default: () => [],
		},
	},
	data () {
		return {
			mq,
			abTest,
			inStockAnywhere,
			formatPrice,
			prerender,
			getStoreSuggestions,
			daysBetweenDates,
			displayStarRating,
			getProductRating,
			convertToCdnUrl,
			getExtraProductData,
			getExtraProductData,
			scrollTo,
			restrictedProducts,
			reviews: [],
			nrReviews: 0, // Number of reviews to show in badges.
			insured: false,
			nrQuestions: 0,
			navSections: [],
			targetScroll: 0,
			renderTips: false,
			clickedBuy: false,
			nrMeterReviews: 0, // Number of reviews to show next to the rating/hype meter.
			reviewsLoaded: null,
			isHeaderHidden: false,
			showingImageIndex: 0,
			selectedImageIndex: 0,
			questionsLoaded: null,
			productCampaigns: [],
			storeKey: 0,
			isOpen: false,
			offers: [],
			// dynamic heights
			headerHeight: 0,
			productNavHeight: 0,
			resursPaymentOptions: [],
			pickSubscription: 0,
			lowestSubscriptionPrice: 0,
		};
	},
	handleTakeovers: true,

	computed: {
		iframeStyle () {
			return {
				'max-height': '-webkit-fill-available',
				'height': '95vh',
				'width': '100vw',
			};
		},
		suggestionIconColor: () => '1e222d',
		/**
		 * Category suggestions that show up for discontinued products.
		 */
		categorySuggestions () {
			const suggestions = [];
			let categories = null;

			if (this.product.mainCategoryPath) {
				categories = this.product.mainCategoryPath;
			} else if (this.product.categories) {
				categories = this.product.categories;
			}
			if (categories) {
				for (const category of categories) {
					if (category.active && category.hasProducts) {
						suggestions.push({
							id: category.id,
							name: category.name,
							router: this.$router.route({
								name: 'category',
								params: { id: category.id, name: category.name },
							}),
							icon: category.icon,
						});
					}
				}
			}

			if (this.product.section && this.product.section.active) {
				const section = this.product.section;
				suggestions.push({
					id: section.id,
					name: section.name,
					router: this.$router.route({
						name: 'section',
						params: { id: section.id, name: section.name },
					}),
					icon: section.icon,
				});
			}
			return suggestions;
		},
		showDiscontinuedTips () {
			return (
				this.isTrulyDiscontinued &&
				! this.product.isFyndware &&
				this.lists.length === 1
			);
		},
		/**
		 * Show relevant tips for a discontinued product.
		 */
		hasDiscontinuedTips () {
			return this.showDiscontinuedTips && this.lists.length && this.lists[0].products.length;
		},
		isTrulyDiscontinued () {
			//fundera på om vi vill köra rakt av på int;at discontinued eller inte?
			return this.product.discontinued && ((this.product.discontinued * 1) === 1 || ! this.inStock && this.product.stock?.isTrue);
		},
		manufacturerRoute () {
			return {
				name: 'manufacturer',
				params: {
					id: this.product.manufacturer.id,
					name: this.product.manufacturer.name,
				},
			};
		},
		useProductTipSlider () {
			if (! this.product || ! this.product.id) { // bara för att vi ska tvinga uppdatering av denna när vi byter produkt
				return;
			}
			return window.useProductTipSlider;
		},
		requiresPrepayment () {
			for (const key in this.product.meta) {
				if (key === 'requires_prepayment' && this.product.meta[key]) {
					return true;
				}
			}
			return false;
		},
		isHygieneArticle () {
			return !! this.product.meta.is_hygiene_article;
		},
		showPrice () {
			if (! this.product.price) {
				return false;
			}
			return ! this.product.statusCodes || this.product.statusCodes.indexOf(10 /* kommer snart */) < 0;
		},
		topReviewDisplayProductName () {
			if (! this.product) {
				return false;
			}
			return this.product.variants ? true : false;
		},
		inStock () {
			if (this.product.stock) {
				return inStockAnywhere(this.product.stock);
			}
			return false;
		},
		storeSuggestions () {
			const s = getStoreSuggestions(this.product.stock, this.user.storeSuggestions, this.userSettings.myStores, 4);
			return s;
		},
		hasData () {
			return this.product.data && Object.keys(this.product.data).length;
		},
		showDaysUntilRelease () {
			return this.daysUntilRelease > 0 && this.daysUntilRelease < 100 && this.product.release.format === 'Y-m-d';
		},
		formattedDaysUntilRelease () {
			return this.daysUntilRelease + (this.daysUntilRelease <= 1 ? ' dag ' : ' dagar ') + 'kvar';
		},
		daysUntilRelease () {
			const date = new Date();
			date.setTime(this.product.release.timestamp * 1000);
			const now = new Date();
			return daysBetweenDates(date, now);
		},
		isReleased () {
			if (! this.product.release) {
				return true; // @TODO dubbelkolla
			}
			const date = new Date();
			date.setTime(this.product.release.timestamp * 1000);
			const now = new Date();
			return now >= date;
		},
		hasAvgRating () {
			return this.product.averageRating !== null && this.product.averageRating !== undefined;
		},
		/**
		 * True if stars should be displayed, false if flames should be displayed.
		 */
		displayStars () {
			return displayStarRating(this.product);
		},
		rating () {
			return getProductRating(this.product);
		},
		releaseDate () {
			const date = PHPDate(this.product.release.format, this.product.release.timestamp);
			return date;
		},
		hasCampaignPrice () {
			return this.product.regularPrice && this.product.price.price < this.product.regularPrice.price;
		},
		hasMaxPurchaseLimit () {
			if (this.restrictedProducts.find((id) => id === this.product.id)) {
				return true;
			}
			/*const restrictedCats = [16279, 16308];
			for (const category of this.product.categories) {
				if (restrictedCats.indexOf(category.id) === -1) {
					continue;
				}
				return true;
			}*/
			return false;
			// @TODO finns category_options för detta
		},
		hasLongDeliveryTime () {
			// This method returns true if the stock for this product is empty and the status code of this product is sat to 1 (ID_LONG_DELIVERY_TIME)
			if (this.product.statusCodes === null || this.product.statusCodes === undefined || this.product.statusCodes.length <= 0) {
				return false;
			}
			if (this.product.statusCodes.indexOf(1) !== -1) {
				return true;
			}
		},
		description () {
			if (! this.product || ! this.product.description) {
				return '';
			}
			return this.product.description;
		},
		showOriginalDescriptionPanel () {
			if (this.$refs.originalDescription) {
				return this.$refs.originalDescription.fixedHtml.length > 0;
			}
			return false;
		},
		showHeroMarking () {
			if (this.product.hasOwnProperty('heroMarking') && this.product.heroMarking === true && this.user.showAdminPanel) {
				return true;
			}
			return false;
		},
		canBuy () {
			return ! this.fullyBooked && ! this.isTrulyDiscontinued && ! this.notYetAvailable && (! this.product.minimumRankLevel || this.user.rankLevel > 0 && this.product.minimumRankLevel <= this.user.rankLevel);
		},
		notYetAvailable () {
			return this.product.statusCodes && this.product.statusCodes.indexOf(10/* Kommer snart */) >= 0;
		},
		fullyBooked () {
			return this.product.statusCodes && this.product.statusCodes.indexOf(9/* Tillfälligt fullbokad*/) >= 0;
		},
		priceIconCampaign () {
			return convertToCdnUrl('/img/icons/price/campaign.svg');
		},
		insuranceIcon () {
			return this.product.mainCategoryPath.slice(-1)[0].icon;
		},
		showInsurancePopup () {
			//make sure product has insurance & disable popup if price is less than 300
			return !! this.product.insurance && (parseInt(this.product.price.price)) > 300;
		},
		showAccessoriesPopup () {
			return this.offers.length;
		},
		cursorStyling () {
			let pos = 0;
			let width = 0;
			const offset = this.productNavHeight + this.headerHeight * ! this.isHeaderHidden;

			let index = this.navSections.findIndex((section) => {
				return section.breakpoint >= this.targetScroll + offset + 15;
			});
			index = Math.max(index - 1, 0);

			if (this.navSections.length) {
				pos = this.navSections[index].pos;
				width = this.navSections[index].width;
			}

			return {
				top: ! this.isHeaderHidden * this.headerHeight + 'px',
				'--cursor-position': pos + 'px',
				'--cursor-width': width + 'px',
			};
		},
		productLists () {
			for (const list of this.lists) {
				if (list.products.length) {
					trackImpression(list.products, list.title, { rating: true });
				}
			}
			return this.lists;
		},
		showFlashSale () {
			const today = new Date();
			const saleEnd = new Date(this.product.price.endAt);

			return this.product.price.nearlyOver
				&& today < saleEnd
				&& (((! this.product?.lowestPrice || parseFloat(this.product?.lowestPrice?.price) > parseFloat(this.product.price.price)) && validCampaignLength(this.product.price))
				|| this.product.price.price === this.product.regularPrice.price);
		},
		subscriptionOptions () {
			let info = [];
			if (this.product.phoneSubscription) {
				info = [
					{
						label: 'Bara telefonen',
						rightText: formatProductPrice(this.product.price, this.user.settings.show_prices_excluding_vat),
					},
					{
						label: 'Med abonnemang',
						rightText: this.lowestSubscriptionPrice ?  'från ' + this.lowestSubscriptionPrice + ' kr/mån' : '',
					}
				];
			}
			return info;
		},
		maxQtyPerCustomer () {
			let maxQty = this.product.price?.maxQtyPerCustomer;
			if (! maxQty) {
				maxQty = this.product.saleLimit?.maxQtyPerCustomer; //vilken har prio?, kanske den som är lägst bara? ..lär tänka hur de funkar
			}
			return maxQty;
		},
		...mapState({
			user: (state) => state.user,
			userSettings: (state) => state.userSettings,
			lowestMethodCost: state => state.shippingPrices.lowestShippingPrice,
			priceList: state => state.shippingPrices.priceList,
		}),
		...mapGetters({
			accessories: 'productTips/overflowingLists',
			/*shippingDiscount: 'user/shippingDiscount',*/
			bundleOffers: 'productTips/bundleOffers',
			adminPanel: 'user/isAdminPanelShowing',
			rankLevel: 'user/rankLevel',
			lists: 'productTips/lists',
			cartSum: 'cart/sum',
		}),
	},
	watch: {
		selectedImageIndex (index) {
			const el = this.$refs['thumbnail-gallery'];
			const containerWidth = el.offsetWidth;
			const visibleMinX = el.scrollLeft;
			const imgWidth = 68;
			const imgPos = index * imgWidth;
			let overflow = visibleMinX - imgPos;

			if (overflow > 0) {
				Tween.destroy(el);
				new Tween(el, { scrollLeft: el.scrollLeft - overflow }, 0.2);
				return;
			}

			const visibleMaxX = visibleMinX + containerWidth;
			overflow = imgPos + imgWidth - visibleMaxX;
			if (overflow > 0) {
				Tween.destroy(el);
				new Tween(el, { scrollLeft: el.scrollLeft + overflow }, 0.2);
			}
		},
		adminPanel (to) {
			if (to) {
				getExtraProductData([this.product], this);
			}
		},
		scrollPos (to, from) {
			//Update targetscroll to scrollPos if user scrolls opposite direction.
			if (Math.sign(this.targetScroll - to) === Math.sign(from - to)) {
				this.targetScroll = to;
			}
			if (to === document.body.clientHeight - window.innerHeight) {
				this.targetScroll = to + window.innerHeight;
			}
		},
		docHeight () {
			this.calculateProductNav();
		},
		product: {
			handler (to, frm) {
				this.calculateProductNav();
				this.getProductCampaigns();
				bus.$emit('current.product', this.product);

				if (to !== frm && frm && this.product.phoneSubscription) {
					this.getLowestSubscriptionPrice();
				} else {
					this.lowestSubscriptionPrice = 0;
				}
			},
			immediate: true,
		},
	},
	mounted () {
		bus.$on('loadedProductAgain', (payload) => {
			this.storeKey++;
		});
		bus.$on('header.status', ([hidden, height]) => {
			this.headerHeight = height - 1;
			this.isHeaderHidden = hidden;
		});
		bus.$on('sticky.triggerInsurance', (event) => {
			this.triggerInsurance();
			this.getOffers();
		});
		bus.$on('product-page.add-to-cart', (event) => {
			if (event === 'bundle') {
				this.triggerInsurance();
				this.getOffers();
			}
		});
		this.selectImage(0);
		getExtraProductData([this.product], this);

		this.$nextTick(() => {
			this.calculateProductNav();
		});
		if (this.product.phoneSubscription) {
			this.getLowestSubscriptionPrice();
		} else {
			this.lowestSubscriptionPrice = 0;
		}
	},
	beforeDestroy () {
		window.removeEventListener('message', this.addPhoneSubscriptionCompleted);
	},
	methods: {
		formatProductPrice,
		componentLoaded () {
			if (this.reviewsLoaded && this.questionsLoaded) {
				this.product.loaded = true;
				// Give time for DOM to render.
				Vue.nextTick(() => {
					bus.$emit('product-component:loaded', this.product);
				});
			}
		},
		setRenderTips () {
			this.renderTips = true;
		},
		buyButtonClick (product) {
			// This method is recursively searched for and executed by any containing
			// buy-buttons.
			// @TODO eller ska vi kanske använda events?

			bus.$emit('product-page.add-to-cart', 'main-prod');

			this.clickedBuy = true;
		},
		addPhoneSubscription () {
			const id = this.$store.state.cart.id;
			window.addEventListener('message', this.addPhoneSubscriptionCompleted);

			const trackingData = {
				event: 'initiate_subscription',
				rating: true,
				stock: true,
				coupon: true,
			};

			trackImpression([this.product], 'Product Page', trackingData);

			if (id) {
				this.isOpen = true;
				Vue.nextTick(() => {
					this.$refs.phoneSubscription.src = 'https://abonnemang.webhallen.com/cart/' + id + '/product/' + this.product.id;
				});
			} else {
				waja.get('cart/current')
					.on('success', res => {
						this.isOpen = true,
						Vue.nextTick(() => {
							this.$refs.phoneSubscription.src = 'https://abonnemang.webhallen.com/cart/' + encodeURIComponent(res.data.cartId) + '/product/' + this.product.id;
						});
					}).go();
			}
		},
		peekImage (index) {
			this.showingImageIndex = index;
		},
		stopPeek (event) {
			const el = this.$refs['thumbnail-gallery'];
			if (el.contains(event.toElement)) {
				return;
			}
			this.showingImageIndex = this.selectedImageIndex;
		},
		selectImage (index) {
			this.selectedImageIndex = index;
			this.showingImageIndex = index;
		},
		nextImage () {
			if (this.selectedImageIndex < this.product.images.length - 1) {
				this.selectedImageIndex++;
			}
			this.showingImageIndex = this.selectedImageIndex;
		},
		previousImage () {
			if (this.selectedImageIndex > 0) {
				this.selectedImageIndex--;
			}
			this.showingImageIndex = this.selectedImageIndex;
		},
		onReviewsLoad (data) {
			this.reviews = data.reviews;
			this.nrReviews = data.nrReviews;
			this.nrMeterReviews = data.nrMeterReviews;
			this.reviewsLoaded = true;
			this.componentLoaded();
		},
		onQuestionsLoad (data) {
			this.nrQuestions = data.nrQuestions;
			this.questionsLoaded = true;
			this.componentLoaded();
		},
		t,
		tp: t.prefix('PRODUCT'),
		tr: t.prefix('REVIEW'),
		tg: t.prefix('GSPR'),
		setInsured (insured) {
			this.insured = insured;
		},
		getProductCampaigns () {
			const pass = this.$route.query.pass;
			waja.get('product/campaign').data({'productId': this.product.id, 'pass': pass})
				.on('success', res => {
					if (res.data) {
						this.productCampaigns = res.data;
					}
				})
				.go();
		},
		calculateProductNav () {
			const productNav = this.$refs.productNav;
			if (productNav) {
				let pos = 0;
				this.productNavHeight = productNav.offsetHeight;

				let data = {
					pos: pos,
					width: 0,
					breakpoint: window.pageYOffset,
				};

				this.navSections = Array.from(productNav.children).map((elem) => {
					const child = elem.children[0];
					const section = document.querySelector(child.getAttribute('href'));

					if (child && section) {
						const navRect = child.getBoundingClientRect();
						const sectionRect = section.getBoundingClientRect();

						data = {
							pos: pos,
							width: navRect.width,
							breakpoint: window.pageYOffset + sectionRect.top - 1,
						};

						pos += navRect.width;
					}

					return data;
				});
				this.navSections.push({ breakpoint: Infinity });
			}
		},
		moveTo (to) {
			let verticalOffset = 40;
			this.targetScroll = scrollTo(to, null, true, -verticalOffset);
			if (this.targetScroll < this.scrollPos) {
				verticalOffset = 160;
				this.targetScroll = scrollTo(to, null, true, -verticalOffset);
			}
		},
		staticCampaignLinkText (campaign) {
			return campaign.isDiscounted ? 'Gå till kampanj' : 'Visa fler produkter';
		},
		triggerInsurance () {
			if (this.showInsurancePopup) {
				this.$refs.insurancePopup.modalOpen = true;
			} else if (this.showAccessoriesPopup) {
				this.$refs.showAccessoriesPopup.modalOpen = true;
			}
		},
		triggerAccessoriesPopup () {
			this.$refs.showAccessoriesPopup.modalOpen = true;
		},
		addPhoneSubscriptionCompleted (event) {
			this.isOpen = false;
			this.$store.dispatch('cart/refresh', {checkCart: true});
			window.removeEventListener('message', this.addPhoneSubscriptionCompleted);
		},
		getOffers () {
			if (this.cartNumRows <= 0) {
				return;
			}

			waja.get('product-cart-offer/')
				.data({
					type: 1,
					productId: this.product.id,
					touchpoint: isDesktop() ? 'DESKTOP' : 'MOBILE',
					manufacturer: this.product.manufacturer.id,
				})
				.on('success', res => {
					if (! res.data.hasOwnProperty('products')) {
						return;
					}
					this.offers = res.data.products;
				})
				.go();
		},
		async getLowestSubscriptionPrice () {
			const url = 'https://se.verticula.co/api/contract/cheapest?deviceId=' + this.product.id + '&contractLength=24';
			const response = await fetch(url);
			const data = await response.json();
			if (data?.price) {
				this.lowestSubscriptionPrice = formatPrice(data.price);
			} else {
				this.lowestSubscriptionPrice = 0;
			}
		},
	},
};
</script>